import React, { type ReactNode, useState, useRef, useEffect } from 'react';

import classNames from 'classnames';
import { get, set, capitalize, cloneDeep, omit } from 'lodash';
import { get as apiGet, patch, post } from 'helpers/api';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import Toggle from 'components/toggle';

import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Switch } from '@buildoutinc/blueprint-react/ui/Switch';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';
import { Tabs } from '@buildoutinc/blueprint-react/ui/Tabs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight, faUndo } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight, faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';

import DemographicsMap from './demographics_map';
import OsRingEditor from './os_ring_editor';
import RadiusLegend from './radius_legend';
import { buildOsPin, CLASSIC_RING_COLOR, classicRingStyle, osRingStyle } from './ring_styles';
import type { RingEditorProps } from './types';
import UnsavedDataGuard from './unsaved_data_guard';
import {
  demographicsCategories,
  demographicsFieldDefinitions,
  demographicsFieldsFor,
  MAX_RADII
} from './demographics_constants';
import { showSnackbar } from 'components/ui/snackbar/helper';

import './styles.scss';

type DemographicsReportConfig = {
  categories: Record<string, boolean>;
  company: {
    id: number;
    name: string,
    primaryColor: string;
  },
  configId: number;
  demographicsReportId: number | null;
  demographicsData: Record<string, any>[];
  distanceUnit?: 'mi' | 'km';
  errorSummary?: string | null;
  hiddenFields: Set<string>;
  overrideData?: Record<string, any>[];
  overrideValues?: boolean;
  preview: boolean;
  lastRefreshedAt?: string | null;
  lastRefreshedAgo?: string | null;
  osDemographicDataSection?: (actions: ReactNode, content: ReactNode) => ReactNode;
  osRadiusMapSection?: (actions: ReactNode, content: ReactNode) => ReactNode;
  sourceHtml?: string | null;
  property: {
    id: number;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
  },
  radiusRings: (number | null)[];
  savePath: string;
  showDriveTime: boolean;
  showRadiusRings: boolean;
  status?: 'ready' | 'generating' | 'generate_failed';
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

type DemographicsField = (typeof demographicsFieldDefinitions)[number];

const formatValue = (value: number | null | undefined, format: string) => {
  if (value === null || typeof value === 'undefined') return 'N/A';

  switch (format) {
  case 'currency':
    return `$${Math.round(value).toLocaleString()}`;
  case 'number':
    return value.toLocaleString();
  case 'decimal':
    return value.toFixed(1);
  default:
    return value;
  }
};

function DemographicsTab(props: DemographicsReportConfig) {
  const uiMaxWidth = '80%';

  // Use BO colors on the 'edit screen' and then company colors on the customer facing
  const companyPrimaryColor = props.preview  ? props.company?.primaryColor : CLASSIC_RING_COLOR;
  const lastRefreshed = props.lastRefreshedAt ? new Date(props.lastRefreshedAt) : null;
  const isNewReport = props.demographicsReportId === null;

  const allCategories = demographicsCategories;

  const getCategoryLabel = (field: string) => {
    const category = allCategories.find(cat => cat.field === field);
    return category ? category.label : capitalize(field);
  };

  // helpers to convert data into snake_case from camelCase
  const toSnake = (key: string) => key.replace(/([A-Z])/g, '_$1').toLowerCase();
  const normalizeRings = (rings: any[]): Record<string, any>[] => Array.isArray(rings)
    ? rings.map((r) => {
      if (!r || typeof r !== 'object') return r;
      const out: Record<string, any> = {};
      Object.keys(r).forEach((k) => {
        out[toSnake(k)] = r[k];
      });
      return out;
    })
    : [];

  const [config, setConfig] = useState<DemographicsReportConfig>(props);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [activeInfoCategory, setActiveInfoCategory] = useState<string>('overview');
  const [saveStatus, setSaveStatus] = useState<'success' | 'failure' | null>(null);
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set(props.hiddenFields || []));
  const [useCustomValues, setUseCustomValues] = useState<boolean>(props.overrideValues || false);
  const [customValues, setCustomValues] =
    useState<Record<string, number>[]>(
      normalizeRings((props.overrideValues && props.overrideData) || props.demographicsData || [])
    );
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const isOs = !!props.osDemographicDataSection;
  const [isGenerating, setIsGenerating] = useState<boolean>(isOs && props.status === 'generating');
  const refreshing = isRegenerating || isGenerating;
  const initialStateRef = useRef({
    radiusRings: props.radiusRings ? [...props.radiusRings] : [],
    hiddenRows: new Set(props.hiddenFields || []),
    useCustomValues,
    customValues,
  });
  const tableTopRef = useRef<HTMLDivElement>(null);
  const isDirty = (
    JSON.stringify(config.radiusRings) !== JSON.stringify(initialStateRef.current.radiusRings) ||
    JSON.stringify([...hiddenRows].sort()) !== JSON.stringify([...initialStateRef.current.hiddenRows].sort()) ||
    useCustomValues !== initialStateRef.current.useCustomValues ||
    (useCustomValues && JSON.stringify(customValues) !== JSON.stringify(initialStateRef.current.customValues))
  );
  const radiusChangedRef = useRef<boolean>(false);

  const setRadiusRing = (index: number, value: number | null) => {
    const origRadii = get(config, 'radiusRings', []);
    const newRadii = [...origRadii];
    if (newRadii[index] === value) return;

    newRadii[index] = value;
    radiusChangedRef.current = true;
    applyConfigChange({
      ...config,
      radiusRings: newRadii
    });
  };

  const addRadiusRing = () => {
    if (config.radiusRings.length >= MAX_RADII) return;
    radiusChangedRef.current = true;
    applyConfigChange({
      ...config,
      radiusRings: [...config.radiusRings, null]
    });
    setCustomValues(prev => [...prev, {}]);
  };

  const removeRadiusRing = (index: number) => {
    if (config.radiusRings.length <= 1) return;
    radiusChangedRef.current = true;
    applyConfigChange({
      ...config,
      radiusRings: config.radiusRings.filter((_, i) => i !== index)
    });
    setCustomValues(prev => prev.filter((_, i) => i !== index));
  };

  // A ring is "filled" once the user has entered a positive number. Empty
  // (null) and 0 both block save so newly added rings must get a real value.
  const allRadiiFilled = config.radiusRings.length > 0 &&
    config.radiusRings.every(r => typeof r === 'number' && r > 0);

  const applyConfigChange = (newConfig: DemographicsReportConfig) => {
    setConfig(newConfig);
  };

  const autoSaveTimerRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef<boolean>(false);

  const clearAutoSaveTimer = () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  };

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollForGenerationComplete = (reportId: number) => {
    clearPollTimer();
    setIsGenerating(isOs);
    const started = Date.now();

    const poll = async () => {
      if (Date.now() - started > POLL_TIMEOUT_MS) {
        showSnackbar({
          text: 'Data regeneration is taking longer than expected. Please reload the page manually.',
          variant: 'danger'
        });
        setIsGenerating(false);
        setIsRegenerating(false);
        return;
      }
      const status = await apiGet(`/demographics_reports/${reportId}/status`)
        .then(json => json.status)
        .catch(() => null);
      if (unmountedRef.current) return;

      if (status === 'ready' || status === 'generate_failed') {
        window.removeEventListener('beforeunload', onBeforeUnload);
        window.location.reload();
      } else {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    unmountedRef.current = false;
    if (isGenerating && props.demographicsReportId) pollForGenerationComplete(props.demographicsReportId);

    return () => {
      unmountedRef.current = true;
      clearAutoSaveTimer();
      clearPollTimer();
    };
  }, []);

  const handleSave = async (): Promise<boolean> => {
    setIsSaving(true);
    setSaveStatus(null);

    // Store if radius changed before async operations
    const radiusChanged = radiusChangedRef.current;

    const params = {
      config: {
        categories: config.categories,
        configId: config.configId,
        hiddenFields: Array.from(hiddenRows),
        showDriveTime: config.showDriveTime,
        showRadiusRings: config.showRadiusRings
      },
      demographics_report: {
        measurements: 'imperial',
        override_values: useCustomValues,
        // Rails drops null array slots, which would shift every later ring down one.
        override_data: useCustomValues
          ? config.radiusRings.map((_, i) => omit(customValues[i] ?? {}, 'radius_in_miles'))
          : null,
        // Pad to fixed positional shape with explicit nulls so the backend
        // clears removed slots rather than leaving them at their previous value.
        radius1: config.radiusRings[0] ?? null,
        radius2: config.radiusRings[1] ?? null,
        radius3: config.radiusRings[2] ?? null
      }
    };

    try {
      const response = await (isNewReport ? post : patch)(config.savePath, params);
      if (response && response.success === true) {
        initialStateRef.current = {
          radiusRings: [...config.radiusRings],
          hiddenRows: new Set(hiddenRows),
          useCustomValues,
          customValues,
        };

        if (radiusChanged || isNewReport) {
          radiusChangedRef.current = false;
          showSnackbar({ text: 'Regenerating data…', variant: 'info' });
          pollForGenerationComplete(response.id ?? config.demographicsReportId);

          return true;
        } else {
          showSnackbar({ text: 'Saved successfully', variant: 'success' });
          return true;
        }
      } else {
        setSaveStatus('failure');
        return false;
      }
    } catch (e) {
      showSnackbar({ text: `An error occurred while saving. ${e}`, variant: 'danger' });
      setSaveStatus('failure');
      return false;
    } finally {
      setIsSaving(false);

      setTimeout(() => {
        setSaveStatus(null);
      }, 5000);
    }
  };

  const handleRegenerate = async () => {
    if (isNewReport) {
      if (!allRadiiFilled || isSaving) return;

      setIsRegenerating(true);
      if (!await handleSave()) setIsRegenerating(false);
      return;
    }

    setIsRegenerating(true);

    try {
      const response = await post(`/demographics_reports/${config.demographicsReportId}/regenerate_data`, {});

      if (response && response.success === true) {
        if (isOs) {
          showSnackbar({ text: 'Regenerating data…', variant: 'info' });
          pollForGenerationComplete(config.demographicsReportId as number);
        } else {
          window.location.reload();
        }
      } else {
        showSnackbar({ text: 'Regeneration failed', variant: 'danger' });
      }
    } catch (e) {
      showSnackbar({ text: `Regeneration request failed. ${e}`, variant: 'danger' });
    } finally {
      setIsRegenerating(false);
    }
  };

  const saveButtonDisabled = () => {
    return isSaving || refreshing || (!isDirty && !isNewReport) || !allRadiiFilled;
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h5 style={{ color: 'rgb(28 29 33)', fontWeight: 'bold' }}>Demographics</h5>
          <p style={{ marginBottom: 0 }}>
            Last refreshed: {lastRefreshed ? lastRefreshed.toLocaleDateString() : '—'}
            <a
              aria-disabled={refreshing}
              href="#"
              style={{ cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1 }}
              onClick={(e) => {
                e.preventDefault();
                if (!refreshing) handleRegenerate();
              }}
            >
              {refreshing ? '. Refreshing…' : '. Refresh'}
            </a>
          </p>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
          {saveStatus === 'success' &&
            <div className="text-success">Saved successfully</div>
          }
          {saveStatus === 'failure' &&
            <div className="text-danger" data-testid="failure-message">
              Save failed
            </div>
          }
          <Button
            data-testid="save-button"
            disabled={saveButtonDisabled()}
            style={{ fontWeight: 'normal' }}
            onClick={handleSave}
          >
            {isSaving ? 'Saving ...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    );
  };

  const lastRefreshedText = props.lastRefreshedAgo ? `${props.lastRefreshedAgo} ago` : '—';

  const renderOsActions = () => (
    <div className="d-flex align-items-center gap-3">
      <span className="fs-small text-muted d-flex align-items-center gap-1">
        {refreshing ? 'Refreshing…' : `Last refreshed ${lastRefreshedText}`}
        <Button
          aria-label="Refresh demographic data"
          disabled={refreshing || isDirty}
          disabledTooltip={isDirty ? 'Save your changes before refreshing' : undefined}
          icon={faArrowRotateRight}
          size="sm"
          tooltip="Refresh demographic data"
          variant="ghost"
          onClick={handleRegenerate}
        />
      </span>
      {saveStatus === 'failure' && <div className="text-danger" data-testid="failure-message">Save failed</div>}
      <Button data-testid="save-button" disabled={saveButtonDisabled()} size="sm" onClick={handleSave}>
        {isSaving ? 'Saving ...' : 'Save Changes'}
      </Button>
    </div>
  );

  const setCustomValue = (index: number, dataKey: string, value: number | null) => {
    setCustomValues((prev) => {
      const next = Array.from({ length: Math.max(prev.length, index + 1) }, (_, i) => prev[i] ?? {});
      next[index] = set(cloneDeep(next[index]), dataKey, value);
      return next;
    });
  };

  const customValuesToggle = () => {
    return (
      <div className="d-flex">
        <span className={`me-3${useCustomValues ? ' inactive' : ''}`}>Default Values</span>
        <Toggle
          checked={useCustomValues}
          disabled={isSaving}
          showText={false}
          theme="ds"
          onChange={() => setUseCustomValues(!useCustomValues)}
        />
        <span className={`ms-3${useCustomValues ? '' : ' inactive'}`}>Custom Values</span>
      </div>
    );
  };

  const osCustomValuesSwitch = () => (
    <Field className="gap-2" orientation="horizontal">
      <span className={classNames({ 'text-muted': useCustomValues })}>Default Values</span>
      <Switch checked={useCustomValues} disabled={isSaving || refreshing} onCheckedChange={setUseCustomValues} />
      <Field.Label className={classNames('fw-normal mb-0', { 'text-muted': !useCustomValues })}>
        Custom Values
      </Field.Label>
    </Field>
  );

  const mapProps = {
    distanceUnit: props.distanceUnit,
    latitude: props.property.latitude,
    longitude: props.property.longitude,
    radiusRings: config.radiusRings
  };

  const ringProps: RingEditorProps = {
    radiusRings: config.radiusRings,
    onAddRadiusRing: addRadiusRing,
    onRadiusRingChange: setRadiusRing,
    onRemoveRadiusRing: removeRadiusRing
  };

  const radiusMap = props.osRadiusMapSection ? (
    <div className="row g-3">
      <div className="col-12 col-md-9">
        <DemographicsMap
          {...mapProps}
          className="border rounded"
          pin={buildOsPin}
          ringStyle={osRingStyle}
          style={{ height: '460px' }}
        />
      </div>
      <div className="col-12 col-md-3">
        <OsRingEditor {...ringProps} disabled={refreshing} distanceUnit={props.distanceUnit} />
      </div>
    </div>
  ) : (
    <DemographicsMap
      {...mapProps}
      ringStyle={classicRingStyle(companyPrimaryColor)}
      style={{ width: '100%', height: '560px', marginBottom: '15px' }}
    >
      <RadiusLegend {...ringProps} radiusColor={companyPrimaryColor} />
    </DemographicsMap>
  );

  const renderValue = (ring: Record<string, any>, index: number, field: DemographicsField) => {
    const dataKey = field.dataKey || field.key;
    if (!useCustomValues) return formatValue(get(ring, dataKey), field.format);

    const value = get(customValues[index], dataKey, '');
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => (
      setCustomValue(index, dataKey, e.target.value === '' ? null : Number(e.target.value))
    );

    if (props.osDemographicDataSection) {
      return (
        <Input
          aria-label={`${field.label}, ${ring.label}`}
          className="form-control-sm text-end"
          disabled={isSaving}
          step="any"
          type="number"
          value={value ?? ''}
          onChange={onChange}
        />
      );
    }

    return <input style={{ width: '100%' }} type="number" value={value} onChange={onChange} />;
  };

  const osDataContent = () => {
    const data = normalizeRings(props.demographicsData || []);
    const unit = props.distanceUnit ?? 'mi';
    const rings = useCustomValues
      ? config.radiusRings.map(radius => ({ label: radius == null ? '—' : `${radius} ${unit}` }))
      : data.map(ring => ({ ...ring, label: `${ring.radius_in_miles} mi` }));

    return (
      <>
        <p className="text-muted fs-small mb-0">Rows hidden here are also hidden from exported documents.</p>
        <Tabs
          className="d-flex align-items-start gap-4"
          orientation="vertical"
          value={activeInfoCategory}
          onValueChange={value => setActiveInfoCategory(value as string)}
        >
          <Tabs.List className="flex-shrink-0" orientation="vertical" style={{ width: '160px' }} variant="pills">
            {allCategories.map(category => (
              <Tabs.Tab className="text-start" key={category.field} value={category.field}>{category.label}</Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel className="flex-grow-1" style={{ minWidth: 0 }} value={activeInfoCategory}>
            {rings.length === 0 ? (
              <p className="text-muted mb-0">No data available.</p>
            ) : (
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>Metric</Table.Head>
                    {rings.map((ring, index) => (
                      <Table.Head className="text-end" key={index}>{ring.label}</Table.Head>
                    ))}
                    <Table.Head className="text-end">Visible</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {demographicsFieldsFor(activeInfoCategory).map((field) => {
                    const isHidden = hiddenRows.has(field.key);

                    return (
                      <Table.Row key={field.key}>
                        <Table.Cell className={classNames({ 'text-muted': isHidden })}>{field.label}</Table.Cell>
                        {rings.map((ring, index) => (
                          <Table.Cell className={classNames('text-end', { 'text-muted': isHidden })} key={index}>
                            {renderValue(ring, index, field)}
                          </Table.Cell>
                        ))}
                        <Table.Cell>
                          <div className="d-flex justify-content-end">
                            <Switch
                              aria-label={`${field.label} visible in exported documents`}
                              checked={!isHidden}
                              disabled={isSaving}
                              onCheckedChange={(visible: boolean) => setRowHidden(field.key, !visible)}
                            />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            )}
          </Tabs.Panel>
        </Tabs>
      </>
    );
  };

  const osDataWithStatus = () => {
    if (refreshing) return <LoadingSpinner loadingText="Generating demographic data…" size="lg" skipTopBuffer />;

    return (
      <>
        {props.status === 'generate_failed' && !props.overrideValues && !useCustomValues && (
          <Alert severity="destructive">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <span>{props.errorSummary}</span>
              <Button
                className="flex-shrink-0"
                disabled={isDirty}
                disabledTooltip="Save your changes before trying again"
                icon={faUndo}
                variant="ghost"
                onClick={handleRegenerate}
              >
                Try Again
              </Button>
            </div>
          </Alert>
        )}
        {osDataContent()}
      </>
    );
  };

  const renderInfoCard = () => {
    if (props.osDemographicDataSection) {
      return props.osDemographicDataSection(osCustomValuesSwitch(), osDataWithStatus());
    }

    return (
      <div className="info-content">
        {!props.osRadiusMapSection && radiusMap}
        <div className="info-header">
          <span style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch'
          }}
          >
            <h5
              style={{ color: 'rgb(28 29 33)', fontWeight: 'bold', fontSize: '24px', margin: 0 }}
            >
              {getCategoryLabel(activeInfoCategory)}
            </h5>
            {customValuesToggle()}
          </span>
          <p>Rows hidden will also be hidden in documents.</p>
        </div>
        <div className="info-body">
          { renderSidebar()}
          { infoTable()}
        </div>
      </div>
    );
  };

  const infoTable = () => {
    return (
      <div className="info-table">
        <div className="info-container d-flex flex-column">
          <div className="info-data">
            { infoDataTable() }
          </div>
        </div>
      </div>
    );
  };

  const infoDataTable = () => {
    // Normalize incoming demographic ring data to snake_case
    const rawData = props.demographicsData || [];
    const data = normalizeRings(rawData);
    if (!data || data.length === 0) {
      return <p className="p-3">No data available.</p>;
    }

    return (
      <div className="info-data-table" ref={tableTopRef}>
        <table className="table table-striped">
          <thead>
            <tr>
              <th className="align-middle" style={{ backgroundColor: 'rgb(238, 238, 242)' }}></th>
              {data.map((ring, index) => (
                <th
                  className="align-middle"
                  key={index}
                  style={{ textAlign: 'center', backgroundColor: 'rgb(238, 238, 242)', whiteSpace: 'nowrap' }}
                >
                  {ring.radius_in_miles} Mile{ring.radius_in_miles !== 1 ? 's' : ''}
                </th>
              ))}
              <th className="align-middle" style={{ width: '30px', height: '46px', backgroundColor: 'rgb(238, 238, 242)' }}></th>
            </tr>
          </thead>
          <tbody>
            {demographicsFieldsFor(activeInfoCategory).map((field) => {
              const isHidden = hiddenRows.has(field.key);

              return (
                <tr className={isHidden ? 'row-hidden' : ''} key={field.key}>
                  <td
                    className="description-cell align-middle"
                    title={field.label}
                  >
                    {field.label}
                  </td>
                  {data.map((ring, index) => (
                    <td className="data-cell align-middle" key={index} style={{ textAlign: 'center' }}>
                      {renderValue(ring, index, field)}
                    </td>
                  ))}
                  <td className="text-center">
                    <FontAwesomeIcon
                      className="visibility-toggle"
                      icon={isHidden ? faEyeSlash : faEye}
                      style={{ cursor: 'pointer', width: '2em', fontSize: '1.5em' }}
                      title={isHidden ? 'Click to show this row' : 'Click to hide this row'}
                      onClick={() => setRowHidden(field.key)}
                    />
                  </td>
                </tr>
              );
            })}
            {hiddenRows.size > 0 && (
              <tr className="hidden-rows-summary">
                <td className="text-center" colSpan={data.length + 2}>
                  <div className="mt-2 mb-2">
                    <span>{hiddenRows.size} row{hiddenRows.size !== 1 ? 's' : ''} hidden</span>
                    <button
                      className="btn btn-sm btn-link"
                      onClick={() => setHiddenRows(new Set())}
                    >
                      Show all rows
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSidebar = () => {
    const navItems = allCategories;

    return (
      <div className="info-sidebar">
        <div className="categories-list">
          {navItems.map(cat => (
            <div
              className="category-row"
              key={cat.field}
              onClick={() => setActiveInfoCategory(cat.field)}
            >
              <div className="d-flex align-items-center">
                <span className={`category-radio ${activeInfoCategory === cat.field ? 'checked' : ''}`} />
                <span className="category-label">{cat.label}</span>
              </div>
              <FontAwesomeIcon icon={faChevronRight} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Make sure the onBeforeUnload function is defined outside the useEffect so we can reference it
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (isDirty) {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const setRowHidden = (rowKey: string, hidden?: boolean) => {
    setHiddenRows((prevHidden) => {
      const newHidden = new Set(prevHidden);
      if (hidden ?? !prevHidden.has(rowKey)) {
        newHidden.add(rowKey);
      } else {
        newHidden.delete(rowKey);
      }
      return newHidden;
    });
  };

  return (
    <div className="d-flex flex-column align-items-center">
      <UnsavedDataGuard
        message="You have unsaved changes. Are you sure you want to leave this page?"
        when={isDirty}
      />
      <div
        className="d-flex flex-column"
        style={{ gap: '2rem', width: '100%', maxWidth: props.osRadiusMapSection ? undefined : uiMaxWidth }}
      >
        {props.osRadiusMapSection ? props.osRadiusMapSection(renderOsActions(), radiusMap) : renderHeader()}
        { renderInfoCard() }
      </div>
      <div className="text-center" style={{ color: '#9AA0A6', fontSize: '12px', marginTop: '10px' }}>
        <span dangerouslySetInnerHTML={{ __html: props.sourceHtml || '' }} />
      </div>
    </div>
  );
}

export default DemographicsTab;
